<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted } from "vue";
import DiamondsSearchResultDiamond from "./result/diamondsSearchResultDiamond.vue";
import DiamondsSearchResultLoading from "./result/diamondsSearchResultLoading.vue";
import DiamondsSearchResultEmpty from "./result/diamondsSearchResultEmpty.vue";

const props = defineProps<{
  activeFilters: any;
}>();


const diamonds = ref<any[]>([]);
const currentPage = ref(1);
const lastPage = ref(1);
const totalDiamonds = ref(0);
const isLoading = ref(false);
const isAppending = ref(false);
let controller: AbortController | null = null;

const fetchDiamonds = async (append = false) => {
  if (controller) {
    controller.abort();
  }
  controller = new AbortController();
  const signal = controller.signal;

  if (!append) {
    currentPage.value = 1;
    // We don't clear diamonds immediately here to avoid layout jump if it's very fast,
    // but the issue says "If the filter change the current diamonds should be removed"
    diamonds.value = [];
  }
  isLoading.value = true;
  isAppending.value = append;

  try {
    const requestBody: any = {
      type: props.activeFilters.type || "earth",
      size: {
        from: props.activeFilters.size?.min || 0,
        till: props.activeFilters.size?.max >= 10 ? 1000: props.activeFilters.size?.max || 0,
      },
      form: props.activeFilters.form ? [props.activeFilters.form] : [],
      color: props.activeFilters.color ? props.activeFilters.color : [],
      clarity: props.activeFilters.clarity ? props.activeFilters.clarity : [],
      cut: props.activeFilters.cut ? props.activeFilters.cut : [],
      polish: props.activeFilters.polish ? props.activeFilters.polish : [],
      symmetry: props.activeFilters.symmetry ? props.activeFilters.symmetry : [],
      fluorescence: props.activeFilters.fluorescence ? props.activeFilters.fluorescence : [],
      price: {
        from: props.activeFilters.price?.min || 0,
        till: props.activeFilters.price?.max || 0,
      },
      institute: props.activeFilters.institute ? props.activeFilters.institute : [],
      fastDelivery: props.activeFilters.fastDelivery || false,
      page: {
        current: currentPage.value,
        results: 16,
      },
      order: {
        by: "price",
        sort: props.activeFilters.sortOrder || "asc",
      },
      colored: props.activeFilters.colored || false,
      fancy_intensity: props.activeFilters.colored && props.activeFilters.fancy_intensity ? props.activeFilters.fancy_intensity : [],
    };

    const response = await fetch(`${process.env.VUE_APP_DIAMOND_API}/search`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
      signal,
    });

    if (response.ok) {
      const data = await response.json();
      if (append) {
        diamonds.value = [...diamonds.value, ...data.diamonds];
      } else {
        diamonds.value = data.diamonds;
      }
      lastPage.value = data.page.last;
      totalDiamonds.value = data.page.all;
    }
  } catch (error: any) {
    if (error.name === "AbortError") return;
    console.error("Error fetching diamonds:", error);
  } finally {
    if (!signal.aborted) {
      isLoading.value = false;
      isAppending.value = false;
    }
  }
};

watch(
  () => props.activeFilters,
  () => {
    currentPage.value = 1;
    fetchDiamonds();
  },
  { deep: true }
);

const loadMore = () => {
  if (currentPage.value < lastPage.value) {
    currentPage.value++;
    fetchDiamonds(true);
  }
};

onMounted(() => {
  fetchDiamonds();
});

onUnmounted(() => {
  if (controller) {
    controller.abort();
  }
});
</script>

<template>
  <div class="w-full">
    <div v-if="diamonds.length > 0 || isLoading" class="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-2">
      <diamonds-search-result-diamond
        v-for="diamond in diamonds"
        :key="diamond.link"
        :diamond="diamond"
      />
      <template v-if="isLoading">
        <diamonds-search-result-loading
          v-for="i in (isAppending ? 3 : 12)"
          :key="i"
        />
      </template>
    </div>
    <diamonds-search-result-empty v-else />
    <div v-if="currentPage < lastPage && !isLoading" class="mt-8 flex justify-center mb-24">
      <button
        @click="loadMore"
        class="bg-[#B0A695] text-white px-8 py-3 uppercase tracking-[0.2em] text-sm hover:bg-[#9F9584] transition-colors"
      >
        Weitere laden
      </button>
    </div>
  </div>
</template>

<style scoped>
</style>